import {createDraftRepository} from '@yandex-delivery/api/repo/draft'
import {useDebouncedFunction} from '@yandex-delivery/hooks'
import {useCallback, useMemo} from 'react'

import type {AppConfig, OrderFormState} from '../../../types'

/** Ответ хука useDefaultValuesAndDraftSync */
type useDefaultValuesAndDraftSyncResponse = {
  /** Дефолтные значения для формы */
  defaultValues: OrderFormState | undefined
  /** Функция для удаления всех значений из persistent storage */
  clearDraftValues: VoidFunction
  /** Функция для обновления значений в persistent storage */
  updateDraftValues: (values: OrderFormState) => void
}

/** Время до обновления данных в сторе после очередного вызова updateValues */
const DEBOUNCE_WAIT_TIME_IN_MS = 100

/**
 * Возвращает номер телефона из паспорта или пустую строку при отсутствии телефона
 */
const getDefaultPhone = (config: AppConfig) => {
  let defaultPhone = config.launch?.passport_account?.details?.phones?.[0]?.phone || ''

  // Если первый символ - плюс, вырезаем его из строки
  if (defaultPhone[0] === '+') {
    defaultPhone = defaultPhone.substring(1)
  }

  return defaultPhone
}

export const {fetchClearDraftSync, fetchPersistDraftSync, fetchRestoreDraftSync} =
  createDraftRepository<OrderFormState>({
    key: '__ya_delivery_order_form_state_bdui_mobile__',
  })

/**
 * Синкает драфтовые значения между persistent storage и локальным стором.
 * Если пользователь что-то внёс в форму заказа во время предыдущего захода,
 * при этом не создал заказ, эти данные сохранятся.
 * @returns Дефолтные значения для формы и функция для обновления этих данных в persistent storage
 */
export const useDefaultValuesAndDraftSync = (config: AppConfig): useDefaultValuesAndDraftSyncResponse => {
  const defaultValues = useMemo(() => {
    let values: OrderFormState = fetchRestoreDraftSync() || {}

    const routeFromUrl = config.initialProps?.bduiInitialRequest?.state.route
    const route = !!config.initialProps?.zoneName || !!config.initialProps?.from ? routeFromUrl : undefined

    if (route) {
      /** заменяю маршрут на тот, что пришел из конфига, если юзер пришел с zoneName в url или from/to в квери */
      values.route = route
    }

    const submodeFromUrl = config.initialProps?.bduiInitialRequest?.state.selected_submode
    const verticalGroupIdFromUrl = config.initialProps?.bduiInitialRequest?.state.vertical_group_id

    const isDifferentSelectedVerticalHasBeenSelected =
      values.selected_submode && submodeFromUrl !== values.selected_submode
    const isDifferentVerticalGroupIdHasBeenSelected =
      values.vertical_group_id && verticalGroupIdFromUrl !== values.vertical_group_id

    if (
      verticalGroupIdFromUrl &&
      submodeFromUrl &&
      isDifferentVerticalGroupIdHasBeenSelected &&
      isDifferentSelectedVerticalHasBeenSelected
    ) {
      /**
       /* обнуляю черновик, если юзер пришел с новым сабмодом и verticalGroupId в url. Это костыль, потому что на бдуе пока невозможно определить,
       /* какие вертикали будут доступны при изменении формы (по России, при включенном бизнесе и т.д.)
       /* пока поддерживаем только вертикали по городу, которые приходят дефолтом
       */
      values = {}
    }

    if (submodeFromUrl && isDifferentSelectedVerticalHasBeenSelected) {
      /**
       /* обнуляю черновик, если юзер пришел с новым сабмодом в url. Это костыль, потому что на бдуе пока невозможно определить,
       /* какие вертикали будут доступны при изменении формы (по России, при включенном бизнесе и т.д.)
       /* пока поддерживаем только вертикали по городу, которые приходят дефолтом
       */
      values = {}
    }

    if (!values.contacts) {
      values.contacts = [{}]
    }

    /** Если телефон не заполнен, кладём туда первый телефон из паспорта */
    values.contacts[0].phone ||= getDefaultPhone(config)

    /**
     * Форма может вместо незаполненных структур выставлять null.
     * Фильтруем контакты, чтобы пустые сущности не оказались в форме
     * */
    if (values?.contacts) {
      values.contacts = values.contacts.filter(Boolean)
    }

    return values
  }, [config])

  const updateValues = useCallback((values: OrderFormState) => {
    // Удаляем поля, которые не нужно сохранять в драфт
    const {payment_info: _paymentInfo, ndd_offer_name: _ndd_offer_name, ...valuesToSave} = values

    fetchPersistDraftSync(valuesToSave)
  }, [])

  const updateValuesDebounced = useDebouncedFunction(updateValues, DEBOUNCE_WAIT_TIME_IN_MS)

  return {defaultValues, clearDraftValues: fetchClearDraftSync, updateDraftValues: updateValuesDebounced}
}
